/**
 * Page context derivation for the floating chat widget.
 * Maps the current URL pathname to a context object with page key,
 * prompt text for the collapsed widget bubble, and visibility flag.
 */

const PAGE_CONTEXTS = [
    {
        pattern: /^\/directory/,
        page: 'directory',
        promptText: 'Need help navigating the ecosystem?',
    },
    {
        pattern: /^\/ecosystem/,
        page: 'ecosystem',
        promptText: 'Questions about ecosystem health?',
    },
    {
        pattern: /^\/connections\/mentors/,
        page: 'mentors',
        promptText: 'Looking for a mentor?',
    },
    {
        pattern: /^\/connections\/partners/,
        page: 'partners',
        promptText: 'Looking for partner organizations?',
    },
    {
        pattern: /^\/opportunities/,
        page: 'opportunities',
        promptText: 'Looking for specific opportunities?',
    },
    {
        pattern: /^\/organizations\/[a-f0-9]+$/i,
        page: 'org-detail',
        promptText: 'Want to know more about this organization?',
    },
];

// Pages where the widget should be hidden
const HIDDEN_PATTERNS = [
    /^\/assistant/,
    /^\/login/,
    /^\/signup/,
    /^\/forgot-password/,
    /^\/create-profile/,
    /^\/admin/,
];

/**
 * Derive page context from the current pathname.
 * @param {string} pathname - Current URL pathname
 * @param {string} [ecosystemName] - Active ecosystem name, used to template
 *   the default "Ask about …" prompt. Falls back to "this ecosystem" in
 *   platform mode or when the ecosystem hasn't resolved yet.
 * @returns {{ page: string, promptText: string, visible: boolean }}
 */
export function getPageContext(pathname, ecosystemName) {
    // Check if widget should be hidden on this page
    if (HIDDEN_PATTERNS.some((p) => p.test(pathname))) {
        return { page: null, promptText: '', visible: false };
    }

    // Find matching page context
    const match = PAGE_CONTEXTS.find((ctx) => ctx.pattern.test(pathname));
    if (match) {
        return {
            page: match.page,
            promptText: match.promptText,
            visible: true,
        };
    }

    // Default for all other public pages: templated per tenant.
    return {
        page: 'general',
        promptText: `Ask about ${ecosystemName || 'this ecosystem'}`,
        visible: true,
    };
}

/**
 * Get the top-level path segment for determining if navigation
 * should reset chat messages.
 * @param {string} pathname
 * @returns {string} The top-level path segment (e.g., "/directory", "/ecosystem")
 */
export function getTopLevelPath(pathname) {
    const segments = pathname.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : '/';
}
